import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { HiOutlineBell } from "react-icons/hi";
import Sidebar from "./Sidebar";
import {
  canUseNotificationContextFilter,
  filterNotificationsByContext,
  contextForRole,
} from "../../utils/notificationContext";

const NotificationSidebar = ({
  isOpen,
  onClose,
  notifications,
  unreadCount,
  recentNotifications,
  handleNotificationClick,
  handleMarkAllAsRead,
  formatNotificationTime,
}) => {
  const authRoles = useSelector((state) => state.auth && state.auth.roles);
  const currentRole = useSelector((state) => state.auth && state.auth.currentRole);
  const showContextFilter = canUseNotificationContextFilter(authRoles);
  // W1-02 + hard split (Marin, 2026-07-30): mobile mirror of the desktop
  // dropdown — the view follows the profile switch, it is not a filter the user
  // has to set again on every surface.
  const contextFilter = contextForRole(currentRole);


  const visibleNotifications = showContextFilter
    ? filterNotificationsByContext(recentNotifications, contextFilter)
    : recentNotifications;

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} side="right">
      <div className="p-4 border-t border border-[#0000001A]">
        {/* Header */}
        <div className="px-4 pb-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Notifications</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>

        {/* The Agency / Personal tab row used to sit here. Removed on
            Marin's call (2026-07-31) — same reasoning as the desktop bell. */}

        {/* Notifications List */}
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          {visibleNotifications.length > 0 ? (
            visibleNotifications.map((notification) => (
              <div
                key={notification.id}
                data-notification-item
                tabIndex={0}
                role="menuitem"
                className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-4 focus:outline-none focus:bg-gray-100 ${
                  notification.read
                    ? "border-transparent"
                    : "border-blue-500 bg-blue-50"
                }`}
                onClick={() => handleNotificationClick(notification.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleNotificationClick(notification.id);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      notification.read ? "bg-gray-300" : "bg-blue-500"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm ${
                        notification.read
                          ? "text-gray-600"
                          : "text-gray-900 font-medium"
                      }`}
                    >
                      {notification.text}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatNotificationTime(notification.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-gray-500">
              <HiOutlineBell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>
                {showContextFilter && contextFilter !== "all"
                  ? "No notifications for this filter"
                  : "No notifications yet"}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 10 && (
          <div className="px-4 pt-3 border-t">
            <Link
              to="/notifications"
              className="block w-full text-center py-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              onClick={onClose}
            >
              View All Notifications ({notifications.length})
            </Link>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default NotificationSidebar;
